import * as React from "react"

import type { Review, ReviewId } from "../data/review-data"

/**
 * Publish/subscribe cache for the review resource.
 *
 * Every mutation writes through the cache; `useReviewQuery` and
 * `useCachedReview` re-render on notification. The API mirrors React
 * Query's `QueryClient` so callers can be lifted to `queryClient.setQueryData`
 * without changing their call sites.
 *
 * Notifications are synchronous. Listeners must be side-effect-free with
 * respect to the cache — writing during a notification will re-enter the
 * loop and starve later listeners.
 */

type Listener = (review: Review) => void

class ReviewCache {
  private readonly reviews = new Map<ReviewId, Review>()
  private readonly listeners = new Map<ReviewId, Set<Listener>>()

  get(id: ReviewId): Review | undefined {
    return this.reviews.get(id)
  }

  set(id: ReviewId, review: Review): void {
    this.reviews.set(id, review)
    this.listeners.get(id)?.forEach((fn) => fn(review))
  }

  update(id: ReviewId, patch: (prev: Review) => Review): Review | null {
    const prev = this.reviews.get(id)
    if (!prev) return null
    const next = patch(prev)
    this.set(id, next)
    return next
  }

  subscribe(id: ReviewId, listener: Listener): () => void {
    let set = this.listeners.get(id)
    if (!set) {
      set = new Set()
      this.listeners.set(id, set)
    }
    set.add(listener)
    return () => {
      set?.delete(listener)
      if (set && set.size === 0) this.listeners.delete(id)
    }
  }
}

export const reviewCache = new ReviewCache()

/**
 * Subscribe a component to the cached review. Re-renders on every write —
 * both local (mutations) and remote (WebSocket invalidation frames applied
 * through `reviewCache.set`).
 */
export function useCachedReview(id: ReviewId | null): Review | null {
  const [snapshot, setSnapshot] = React.useState<Review | null>(() =>
    id ? (reviewCache.get(id) ?? null) : null,
  )

  React.useEffect(() => {
    if (!id) {
      setSnapshot(null)
      return
    }
    setSnapshot(reviewCache.get(id) ?? null)
    return reviewCache.subscribe(id, setSnapshot)
  }, [id])

  return snapshot
}
